import { useEffect, useEffectEvent, useLayoutEffect, useRef } from "react";
import { useChatReference } from "../../hooks/useChatReference";
import { useStreamReveal } from "../../hooks/useStreamReveal";
import { chatLatency } from "../../lib/chatLatency";
import { Markdown } from "../Markdown";
import { Text } from "../Text";
import { MarkdownStyles } from "./MarkdownStyles";

type Props = {
  text: string;
  /** True while the entry is being actively streamed via SSE. */
  streaming?: boolean;
  onAnimatingChange?: (animating: boolean) => void;
  variant?: "text-regular" | "text-small";
  color?: string;
  citationEntryId?: string;
  latencyMessageId?: string;
  resolveFileHref?: (href: string) => string | null;
};

export function TypewriterText({
  text,
  streaming,
  onAnimatingChange,
  variant = "text-regular",
  color,
  resolveFileHref,
  citationEntryId,
  latencyMessageId,
}: Props) {
  const { revealed, animating } = useStreamReveal(text, streaming);
  const resolveReference = useChatReference();
  useLayoutEffect(() => {
    if (latencyMessageId && revealed.trim())
      chatLatency.rendered(latencyMessageId);
  }, [latencyMessageId, revealed]);

  const reportAnimating = useEffectEvent((value: boolean) =>
    onAnimatingChange?.(value)
  );

  // Rows share one callback, so only report transitions this row caused —
  // otherwise a row mounting or unmounting clears the flag for a row that is
  // still animating.
  const reportedRef = useRef(false);
  useEffect(() => {
    if (animating === reportedRef.current) return;
    reportedRef.current = animating;
    reportAnimating(animating);
  }, [animating]);

  useEffect(
    () => () => {
      if (reportedRef.current) reportAnimating(false);
    },
    []
  );

  return (
    <Text variant={variant} style={{ minWidth: 0 }} color={color}>
      <MarkdownStyles
        variant={variant}
        color={color}
        lineHeight={variant === "text-small" ? "16px" : "20px"}
        bulletSpacing="8px"
      >
        <Markdown
          internalHref={resolveFileHref}
          reference={resolveReference}
          citationEntryId={citationEntryId}
          tableControls
        >
          {revealed}
        </Markdown>
      </MarkdownStyles>
    </Text>
  );
}
